/**
 * Lesson Attendance & Schedule Override Endpoints
 *
 * Injected into `attendanceApi` from `attendanceApiBase.ts`.
 * Covers: Attendance Bundle, School Classes, Transfer Student,
 *         Schedule Overrides (Kurzbeschulung), Course Conflicts for KB.
 *
 * @module attendance/api/lessonAttendanceEndpoints
 */

import { attendanceApi } from "./attendanceApiBase";
import { supabase } from "../../../lib/supabase";
import {
  getAttendanceBundle,
  type AttendanceBundle,
  type AttendanceBundleFlags,
} from "../../../lib/attendanceBundle";

// ============================================================================
// Injected Endpoints
// ============================================================================

export const lessonAttendanceEndpointsApi = attendanceApi.injectEndpoints({
  endpoints: (builder) => ({
    getAttendanceBundleQuery: builder.query<
      AttendanceBundle,
      {
        lessonId: string;
        schoolId: string;
        userId?: string | null;
        flags?: AttendanceBundleFlags;
      }
    >({
      async queryFn(args) {
        try {
          const bundle = await getAttendanceBundle(
            args.lessonId,
            args.schoolId,
            args.userId,
            args.flags,
          );
          return { data: bundle };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_r, _e, args) => [
        { type: "AttendanceBundle", id: args.lessonId },
      ],
      keepUnusedDataFor: 60,
    }),

    getSchoolClasses: builder.query<
      { id: string; name: string; grade_level: number | null; year: number | null }[],
      { schoolId: string }
    >({
      async queryFn({ schoolId }) {
        try {
          if (!schoolId) return { data: [] };
          const { data, error } = await supabase
            .from("vw_active_classes" as never)
            .select("id, name, grade_level, year" as never)
            .eq("school_id" as never, schoolId as never)
            .order("name" as never, { ascending: true });
          if (error) return { error };
          return {
            data: ((data || []) as any[]).map(
              (c: { id: string; name?: string; grade_level?: number | null; year?: number | null }) => ({
                id: String(c.id),
                name: String(c.name || ""),
                grade_level: c.grade_level ?? null,
                year: c.year ?? null,
              }),
            ),
          };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_r, _e, args) => [
        { type: "SchoolClasses", id: args.schoolId },
      ],
      keepUnusedDataFor: 300,
    }),

    getCourseGuestCandidates: builder.query<
      { class_candidates?: any[]; search_candidates?: any[] },
      { lessonId: string; query?: string | null }
    >({
      async queryFn({ lessonId, query }) {
        try {
          if (!lessonId) return { data: {} };
          const { data, error } = await supabase.rpc(
            "fn_attendance_course_guest_candidates" as never,
            { p_lesson_id: lessonId, p_query: query || null } as never
          );
          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: String(error.message) } };
          }
          return { data: (data || {}) as any };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      keepUnusedDataFor: 60,
    }),

    // ========================================================================
    // Schedule Overrides (Kurzbeschulung)
    // ========================================================================

    getScheduleOverrides: builder.query<
      Array<{
        id: string;
        student_id: string;
        override_type: string;
        expected_start_time: string;
        expected_end_time: string;
        start_mode: string;
        end_mode: string;
        valid_from: string;
        valid_until: string | null;
        weekdays: number[];
        status: string;
        reason: string | null;
        created_by: string | null;
        created_at: string | null;
      }>,
      { studentIds: string[] }
    >({
      async queryFn({ studentIds }) {
        try {
          if (!studentIds.length) return { data: [] };
          const { data, error } = await supabase
            .from("student_schedule_overrides" as never)
            .select(
              "id, student_id, override_type, expected_start_time, expected_end_time, start_mode, end_mode, valid_from, valid_until, weekdays, status, reason, created_by, created_at" as never,
            )
            .in("student_id" as never, studentIds as never)
            .is("deleted_at" as never, null as never)
            .eq("status" as never, "aktiv" as never);
          if (error)
            return {
              error: { status: "CUSTOM_ERROR", error: String(error.message) },
            };
          return { data: (data || []) as any[] };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: ["ScheduleOverrides"],
      keepUnusedDataFor: 120,
    }),

    getAllActiveScheduleOverrides: builder.query<
      Array<{
        id: string;
        student_id: string;
        school_id: string;
        override_type: string;
        expected_start_time: string;
        expected_end_time: string;
        start_mode: string;
        end_mode: string;
        valid_from: string;
        valid_until: string | null;
        weekdays: number[];
        status: string;
        reason: string | null;
        student_name: string;
        class_name: string | null;
        created_by_name: string | null;
        created_at: string | null;
        course_conflicts: Array<{
          courseName: string;
          dayLabel: string;
          time: string;
        }> | null;
      }>,
      { classId?: string | null }
    >({
      async queryFn({ classId }) {
        try {
          const { data, error } = await supabase.rpc(
            "fn_get_active_kb_overrides" as never,
            {
              p_class_id: classId || null,
            } as never,
          );

          if (error)
            return {
              error: { status: "CUSTOM_ERROR", error: String(error.message) },
            };

          const rows = Array.isArray(data) ? data : [];
          const result = rows.map((row: any) => ({
            id: row.id,
            student_id: row.student_id,
            school_id: row.school_id,
            override_type: row.override_type,
            expected_start_time: row.expected_start_time,
            expected_end_time: row.expected_end_time,
            start_mode: row.start_mode || 'fixed',
            end_mode: row.end_mode || 'fixed',
            valid_from: row.valid_from,
            valid_until: row.valid_until,
            weekdays: row.weekdays || [],
            status: row.status,
            reason: row.reason,
            student_name: row.student_name || "Unbekannt",
            class_name: row.class_name || null,
            created_by_name: row.created_by_name || null,
            created_at: row.created_at || null,
            course_conflicts: row.course_conflicts || null,
          }));

          return { data: result };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: ["ScheduleOverrides"],
      keepUnusedDataFor: 120,
    }),

    upsertScheduleOverride: builder.mutation<
      { id: string },
      {
        id?: string;
        studentId: string;
        schoolId: string;
        expectedStartTime: string;
        expectedEndTime: string;
        startMode?: 'fixed' | 'school_day_start';
        endMode?: 'fixed' | 'school_day_end';
        validFrom: string;
        validUntil?: string | null;
        weekdays?: number[];
        reason?: string;
        createdBy?: string;
      }
    >({
      async queryFn(params) {
        try {
          if (params.id) {
            const { data, error } = await supabase
              .from("student_schedule_overrides" as never)
              .update({
                expected_start_time: params.expectedStartTime,
                expected_end_time: params.expectedEndTime,
                start_mode: params.startMode || 'fixed',
                end_mode: params.endMode || 'fixed',
                valid_from: params.validFrom,
                valid_until: params.validUntil || null,
                weekdays: params.weekdays || [1, 2, 3, 4, 5],
                reason: params.reason || "Kurzbeschulung",
                updated_at: new Date().toISOString(),
              } as never)
              .eq("id" as never, params.id as never)
              .select("id" as never)
              .single();
            if (error)
              return {
                error: { status: "CUSTOM_ERROR", error: String(error.message) },
              };
            return { data: { id: (data as any).id } };
          } else {
            const { data, error } = await supabase
              .from("student_schedule_overrides" as never)
              .insert({
                student_id: params.studentId,
                school_id: params.schoolId,
                override_type: "kurzbeschulung",
                expected_start_time: params.expectedStartTime,
                expected_end_time: params.expectedEndTime,
                start_mode: params.startMode || 'fixed',
                end_mode: params.endMode || 'fixed',
                valid_from: params.validFrom,
                valid_until: params.validUntil || null,
                weekdays: params.weekdays || [1, 2, 3, 4, 5],
                status: "aktiv",
                reason: params.reason || "Kurzbeschulung",
                created_by: params.createdBy || null,
              } as never)
              .select("id" as never)
              .single();
            if (error)
              return {
                error: { status: "CUSTOM_ERROR", error: String(error.message) },
              };
            return { data: { id: (data as any).id } };
          }
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["ScheduleOverrides", "Fehlzeiten"],
    }),

    deactivateScheduleOverride: builder.mutation<void, { id: string }>({
      async queryFn({ id }) {
        try {
          const { error } = await supabase
            .from("student_schedule_overrides" as never)
            .update({
              status: "beendet",
              updated_at: new Date().toISOString(),
            } as never)
            .eq("id" as never, id as never);
          if (error)
            return {
              error: { status: "CUSTOM_ERROR", error: String(error.message) },
            };
          return { data: undefined };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["ScheduleOverrides", "Fehlzeiten"],
    }),

    // Patches only valid_until on an existing override. Used by the
    // "successor" flow in KurzbeschulungDialog to close out a predecessor
    // entry on a specific date without touching times/weekdays/reason.
    setScheduleOverrideValidUntil: builder.mutation<
      void,
      { id: string; validUntil: string }
    >({
      async queryFn({ id, validUntil }) {
        try {
          const { error } = await supabase
            .from("student_schedule_overrides" as never)
            .update({
              valid_until: validUntil,
              updated_at: new Date().toISOString(),
            } as never)
            .eq("id" as never, id as never);
          if (error)
            return {
              error: { status: "CUSTOM_ERROR", error: String(error.message) },
            };
          return { data: undefined };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      invalidatesTags: ["ScheduleOverrides", "Fehlzeiten"],
    }),

    // ========================================================================
    // Transfer & Course Conflicts
    // ========================================================================

    transferStudent: builder.mutation<
      {
        success: boolean;
        from_deleted: boolean;
        to_upserted: boolean;
        daily_log_id: string;
      },
      {
        studentId: string;
        fromLessonId: string;
        toLessonId: string;
        /**
         * Attendance status to write on the target lesson. Pass `null`
         * for the "pending" / Ausstehend mode — the RPC skips the target
         * SAL write and records a `kind='transferred_back'` row in
         * `lesson_guest_students` instead, so the home teacher decides
         * the final status. Default `'present'` keeps the legacy behavior.
         */
        status?: string | null;
        reason?: string;
      }
    >({
      async queryFn({
        studentId,
        fromLessonId,
        toLessonId,
        status,
        reason,
      }) {
        // Only fall back to 'present' when the caller didn't pass a value
        // at all. Explicit `null` means pending mode and must reach the RPC.
        const effectiveStatus = status === undefined ? "present" : status;
        try {
          const { data, error } = await supabase.rpc(
            "fn_attendance_transfer_student" as never,
            {
              p_student_id: studentId,
              p_from_lesson_id: fromLessonId,
              p_to_lesson_id: toLessonId,
              p_status: effectiveStatus,
              p_reason: reason || null,
            } as never,
          );
          if (error)
            return {
              error: { status: "CUSTOM_ERROR", error: String(error.message) },
            };
          return { data: data as any };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      // No invalidatesTags — refresh is handled explicitly via reloadBundle()
      // to avoid cascading re-fetches of timetable queries.
    }),

    addCourseGuestEnrollment: builder.mutation<
      {
        course_id: string;
        lesson_id: string;
        reason: string;
        results: Array<{
          action: "created" | "existing";
          enrollment_id: string;
          student_id: string;
          course_id: string;
          start_date: string;
          end_date: string;
          is_guest: boolean;
          guest_reason?: string | null;
        }>;
      },
      {
        lessonId: string;
        studentIds: string[];
        reason: string;
      }
    >({
      async queryFn({ lessonId, studentIds, reason }) {
        try {
          const { data, error } = await supabase.rpc(
            "fn_attendance_add_course_guest_enrollment" as never,
            {
              p_lesson_id: lessonId,
              p_student_ids: studentIds,
              p_reason: reason,
            } as never,
          );
          if (error) {
            return {
              error: { status: "CUSTOM_ERROR", error: String(error.message) },
            };
          }
          return { data: data as any };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      // No invalidatesTags — caller should use reloadBundle() for a single-source refresh.
    }),

    removeCourseGuestEnrollment: builder.mutation<
      {
        action: "deleted" | "noop";
        student_id: string;
        course_id: string;
        date: string;
        deleted_rows: number;
      },
      {
        lessonId: string;
        studentId: string;
      }
    >({
      async queryFn({ lessonId, studentId }) {
        try {
          const { data, error } = await supabase.rpc(
            "fn_attendance_remove_course_guest_enrollment" as never,
            {
              p_lesson_id: lessonId,
              p_student_id: studentId,
            } as never,
          );
          if (error) {
            return {
              error: { status: "CUSTOM_ERROR", error: String(error.message) },
            };
          }
          return { data: data as any };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      // No invalidatesTags — caller should use reloadBundle() for a single-source refresh.
    }),

    setCourseShortNoticeCancelled: builder.mutation<
      {
        success: boolean;
        lesson_id: string;
        is_cancelled: boolean;
        reason: string | null;
      },
      {
        lessonId: string;
        isCancelled: boolean;
        reason?: string | null;
      }
    >({
      async queryFn({ lessonId, isCancelled, reason }) {
        try {
          const { data, error } = await supabase.rpc(
            "fn_attendance_set_course_short_notice_cancelled" as never,
            {
              p_lesson_id: lessonId,
              p_is_cancelled: isCancelled,
              p_reason: reason || null,
            } as never,
          );
          if (error) {
            return {
              error: { status: "CUSTOM_ERROR", error: String(error.message) },
            };
          }
          return { data: data as any };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      // Caller triggers explicit reloadBundle().
    }),


  }),
  overrideExisting: false,
});

// ============================================================================
// Generated Hooks
// ============================================================================

export const {
  useGetAttendanceBundleQueryQuery,
  useLazyGetAttendanceBundleQueryQuery,
  useGetSchoolClassesQuery,
  useGetCourseGuestCandidatesQuery,
  useGetScheduleOverridesQuery,
  useGetAllActiveScheduleOverridesQuery,
  useLazyGetAllActiveScheduleOverridesQuery,
  useUpsertScheduleOverrideMutation,
  useDeactivateScheduleOverrideMutation,
  useSetScheduleOverrideValidUntilMutation,
  useTransferStudentMutation,
  useAddCourseGuestEnrollmentMutation,
  useRemoveCourseGuestEnrollmentMutation,
  useSetCourseShortNoticeCancelledMutation,
} = lessonAttendanceEndpointsApi;
